import { get } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'react-apollo';
import { WrappedComponentProps, injectIntl } from 'react-intl';

import {
  getFriendlyValuesFromFlattenedLookup,
  convertLookupTypeOptions,
  getFormattedArrayDateTime,
  getSelectOptionsWithData,
  getFormattedArrayNumber,
  getFormattedArrayCurrency,
  getFormattedJoinedFormula,
  isAbsentCellValue,
  getStatusOptionsWithData,
} from '../../../../helpers';

import ElementFieldSelect from '../FieldSelect';
import ElementFieldRecordReference from '../FieldRecordRef';
import ElementFieldAttachment from '../FieldAttachment';
import ElementFieldCheckbox from '../FieldCheckbox';
import ElementFieldText from '../FieldText';
import ElementFieldCollaborator from '../FieldCollaborator';

import { IRootState } from '../../../../modules/stores';
import { ElementTypes } from '../../../../types/element';
import { ImageDisplayType, TableModeType, ITableConfig } from '../../../../types/space';
import { FIELD_ELEMENT_TYPE_MAP } from '../../../../constants/field';
import { ISelect } from '@treelab/panda';
import { IStatus } from '@treelab/common-logic';

type IConnectProps = ReturnType<typeof mapState>;
interface IProps extends WrappedComponentProps {
  // ID field only comes from top level. So we will not use it if it isInTable = true
  id?: string;
  fieldId: string;
  recordId: string;
  config?: ITableConfig;
  isInTable?: boolean;
  showRefAsText?: boolean;
  depth?: number;
  spaceId: string;
}

const FieldLookup: React.FC<IProps & IConnectProps> = ({
  config,
  cell,
  field,
  locale,
  id,
  isInTable = false,
  showRefAsText = false,
  recordId,
  depth,
  spaceId,
  attachmentHeight,
  intl: { formatMessage },
}) => {
  if (isAbsentCellValue(cell)) return null;
  /**
   * Lookup is a special case because you also have to be wary if there is a lookup inside of a table.
   * Hence, the `isInTable` boolean. If its in the table, it will either be passed from `content/index.tsx` or
   * `refRenderer/index.tsx`
   */
  const lookupConfig = convertLookupTypeOptions(field.typeOptions);
  if (!lookupConfig) return null;

  const elementType = FIELD_ELEMENT_TYPE_MAP[lookupConfig.type];
  const lookupValue = getFriendlyValuesFromFlattenedLookup(cell, field.typeOptions) || [];

  switch (elementType) {
    case ElementTypes.FieldText:
    case ElementTypes.FieldLongText:
    case ElementTypes.FieldPhone:
    case ElementTypes.FieldEmail:
    case ElementTypes.FieldAutoNumber:
    case ElementTypes.FieldRollup:
      return <ElementFieldText.Raw value={lookupValue} />;
    case ElementTypes.FieldFormula:
      const formulaTO = convertLookupTypeOptions(field.typeOptions);
      return (
        <ElementFieldText.Raw
          value={getFormattedJoinedFormula(lookupValue, formulaTO as any, locale)}
        />
      );
    case ElementTypes.FieldNumber:
      const numTO = convertLookupTypeOptions(field.typeOptions);

      return <ElementFieldText.Raw value={getFormattedArrayNumber(lookupValue, numTO as any)} />;
    case ElementTypes.FieldCurrency:
      const currTO = convertLookupTypeOptions(field.typeOptions);

      return <ElementFieldText.Raw value={getFormattedArrayCurrency(lookupValue, currTO as any)} />;
    case ElementTypes.FieldDateTime:
      const dateTO = convertLookupTypeOptions(field.typeOptions);

      return (
        <ElementFieldText.Raw
          value={getFormattedArrayDateTime(lookupValue, dateTO as any, locale)}
        />
      );
    case ElementTypes.FieldRating:
      return <ElementFieldText.Raw value={lookupValue.join(', ')} />;
    case ElementTypes.FieldSelect:
    case ElementTypes.FieldMultiSelect:
      const selTO = convertLookupTypeOptions(field.typeOptions);
      const optsWithData = getSelectOptionsWithData(lookupValue, selTO.options as ISelect[]);

      return <ElementFieldSelect.Raw items={optsWithData} />;
    case ElementTypes.FieldStatus: {
      const selTO = convertLookupTypeOptions(field.typeOptions);
      const optsWithData = getStatusOptionsWithData(lookupValue, selTO.statuses as IStatus[]);

      return <ElementFieldSelect.StatusRaw items={optsWithData} />;
    }
    case ElementTypes.FieldCheckbox:
      return <ElementFieldCheckbox.MultiRaw value={lookupValue} />;
    case ElementTypes.FieldMultiAttachment:
      return isInTable ? (
        <ElementFieldAttachment.TableRaw attachments={lookupValue} height={attachmentHeight} />
      ) : (config as any).imageDisplay === ImageDisplayType.First ? (
        <ElementFieldAttachment.SingleRaw
          attachment={get(lookupValue, '[0].url')}
          height={attachmentHeight}
        />
      ) : (
        <ElementFieldAttachment.MultiRaw attachments={lookupValue} height={attachmentHeight} />
      );
    case ElementTypes.FieldRecordRef:
      return showRefAsText ? (
        <ElementFieldRecordReference.TextForLookup foreignRow={lookupValue} />
      ) : !config ? null : config.tableMode === TableModeType.Table ? (
        <ElementFieldRecordReference.TableForLookup
          id={!isInTable ? id : undefined}
          fieldId={field.id}
          lookupValue={lookupValue}
          recordId={recordId}
          config={config as ITableConfig}
          depth={depth}
          spaceId={spaceId}
        />
      ) : (
        <ElementFieldRecordReference.ListForLookup
          id={!isInTable ? id : undefined}
          fieldId={field.id}
          lookupValue={lookupValue}
          recordId={recordId}
          config={config as ITableConfig}
          depth={depth}
          spaceId={spaceId}
        />
      );
    case ElementTypes.FieldCollaborator:
      return <ElementFieldCollaborator fieldId={field.id} recordId={recordId} />;
    default:
      return <ElementFieldText.Raw value={formatMessage({ id: 'space.error.unsupported' })} />;
  }
};

const mapState = ({ core, space }: IRootState, { recordId, fieldId, spaceId }: IProps) => {
  const currentSpace = space.byId[spaceId];
  const field = core.fieldById[fieldId];
  const finalId = field?.typeOptions.foreignLookupColumnId;
  const attachmentHeight = get(currentSpace, 'config.attachmentHeight')?.[finalId ?? ''];

  return {
    cell: core.recordById[recordId]?.cells[fieldId],
    field: core.fieldById[fieldId],
    locale: core.locale,
    attachmentHeight,
  };
};

export default compose(injectIntl, connect(mapState))(FieldLookup);
